// The two delivery shifts the DMS agrees within (DMS-47).
//
// The customer picks a shift in the app, so every screen that schedules offers
// the same two choices instead of a grid of half hours: a promise of "between
// 8 and 12" is what the operation can keep.
//
// What travels to the API is the day and the shift's code — never an instant.
// The backend resolves the hours in Guatemala time, which is why none of this
// has to know the operator's own zone.

import { minutesLabel } from "./slots";

export type ShiftCode = "morning" | "afternoon";

/** The clock that splits the morning from the afternoon (card-core, P8). */
export const SHIFT_BOUNDARY_MINUTES = 12 * 60;

/** Where the day's deliveries start and stop (07:00 / 20:00).
 *
 *  20:00 is the operation's lateness cut-off, not a promise: the afternoon is
 *  agreed as "from 12:00 onwards" and its label carries no end hour. The
 *  backend stores it as delivery_window_end_at so a route does not call an
 *  afternoon delivery late from its first minute. */
export const SHIFT_DAY_START_MINUTES = 7 * 60;
export const SHIFT_DAY_END_MINUTES = 20 * 60;

export interface DeliveryShift {
  code: ShiftCode;
  titleKey: string;
  /** Short name for a row where the full one does not fit. */
  shortKey: string;
  rangeKey: string;
  /** First minute of the shift: what decides whether it has already passed. */
  startsAt: string;
  startsAtMinutes: number;
  /** Last minute: where the promise ends. Shown, never sent. */
  endsAt: string;
  /** The same, in minutes: what decides whether a shift can still be booked.
   *  A shift is bookable while its WINDOW is open, not until its start —
   *  at 16:00 the afternoon is still deliverable (DMS-47). */
  endsAtMinutes: number;
}

export const DELIVERY_SHIFTS: DeliveryShift[] = [
  {
    code: "morning",
    titleKey: "shift_morning",
    shortKey: "shift_morning_short",
    rangeKey: "shift_morning_range",
    startsAt: minutesLabel(SHIFT_DAY_START_MINUTES),
    startsAtMinutes: SHIFT_DAY_START_MINUTES,
    endsAt: minutesLabel(SHIFT_BOUNDARY_MINUTES),
    endsAtMinutes: SHIFT_BOUNDARY_MINUTES,
  },
  {
    code: "afternoon",
    titleKey: "shift_afternoon",
    shortKey: "shift_afternoon_short",
    rangeKey: "shift_afternoon_range",
    startsAt: minutesLabel(SHIFT_BOUNDARY_MINUTES),
    startsAtMinutes: SHIFT_BOUNDARY_MINUTES,
    endsAt: minutesLabel(SHIFT_DAY_END_MINUTES),
    endsAtMinutes: SHIFT_DAY_END_MINUTES,
  },
];

/** The shift a stored start time belongs to, for printing a choice back. */
export const shiftOf = (startsAt: string): DeliveryShift | undefined =>
  DELIVERY_SHIFTS.find((s) => s.startsAt === startsAt);

/** The shift by its code, for reading one the API sent back. */
export const shiftByCode = (code: string | null | undefined): DeliveryShift | undefined =>
  DELIVERY_SHIFTS.find((s) => s.code === code);

/** How a schedule reads anywhere it is shown — the ambassador's route included:
 *  the shift over its window ("Mañana · 7:00–11:59"), or the exact hour when
 *  that is what was agreed.
 *
 *  The window comes from the translated range, not from startsAt/endsAt: the
 *  afternoon has no end to show ("de 12:00 en adelante"), because 20:00 is the
 *  operation's cut-off and never something the customer was promised. */
export const scheduleLabel = (
  shiftCode: string | null | undefined,
  exact: string,
  t: (key: string) => string,
): string => {
  const shift = shiftByCode(shiftCode);
  return shift ? `${t(shift.shortKey)} · ${t(shift.rangeKey)}` : exact;
};

/** Whether a shift can still be taken on a given day: its window has to be
 *  still open. Comparing against the START is what the half-hour grid used to
 *  do, and it left the ambassador with no bookable shift at all from 12:01
 *  onwards — the afternoon runs to 20:00 and is deliverable all the way.
 *
 *  `nowMinutes` is minutes since local midnight, or null when the day is not
 *  today (a future day has both shifts open).
 */
export const isShiftOpen = (shift: DeliveryShift, nowMinutes: number | null): boolean =>
  nowMinutes === null || shift.endsAtMinutes > nowMinutes;

/** Minutes since local midnight, for isShiftOpen. */
export const minutesOfDay = (d: Date): number => d.getHours() * 60 + d.getMinutes();
